<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="100%"
    top="0px"
    :modal="false"
    append-to-body
    custom-class="video-player-dialog"
    @close="handleClose">
    <div class="video-dialog-wrapper"
         ref="videoDialogWrapper"
         tabindex="-1"
         @keydown.space.prevent="togglePlay"
         @keydown.r.prevent="rotateVideo"
         @keydown.esc="closeDialog"
         @wheel.prevent="onVideoWheel">
      <div v-if="videos.length > 1"
           class="video-nav-arrow video-nav-prev"
           :class="{ disabled: currentIndex === 0 }"
           @click="prevVideo">
        <i class="el-icon-arrow-left"></i>
      </div>
      <div class="video-dialog-content"
           :class="{ 'is-dragging': isDragging }"
           @mousedown="onDragStart"
           @mousemove="onDragMove"
           @mouseup="onDragEnd"
           @mouseleave="onDragEnd">
        <div class="video-transform-wrapper"
             :class="{ 'no-transition': isDragging || instantReset }"
             :style="{ transform: `scale(${scale}) translate(${translateX}px, ${translateY}px) rotate(${rotation}deg)` }">
          <video
            v-if="currentUrl"
            ref="videoPlayer"
            :src="currentUrl"
            autoplay
            class="video-player"
            @timeupdate="onTimeUpdate"
            @loadedmetadata="onLoadedMeta"
            @play="isPlaying = true"
            @pause="isPlaying = false"
            @ended="onVideoEnded">
          </video>
        </div>
      </div>
      <!-- 底部自定义控制条 -->
      <div class="custom-video-controls">
        <div class="control-btn"
             @click="togglePlay"
             title="播放/暂停（按 空格 键切换）">
          <svg v-if="!isPlaying" viewBox="0 0 24 24" width="32" height="32" style="display:block;" fill="#fff" stroke="#fff" stroke-width="2" stroke-linejoin="round">
            <path d="M6 5 L19 12 L6 19 Z" />
          </svg>
          <svg v-else viewBox="0 0 24 24" width="32" height="32" style="display:block;" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round">
            <line x1="7" y1="5" x2="7" y2="19" />
            <line x1="17" y1="5" x2="17" y2="19" />
          </svg>
        </div>
        <div class="progress-track"
             @click="seekVideo">
          <div class="progress-fill"
               :style="{ width: videoDuration ? (videoCurrentTime / videoDuration * 100) + '%' : '0%' }"></div>
        </div>
        <span class="time-text">{{ formatVideoTime(videoCurrentTime) }} / {{ formatVideoTime(videoDuration) }}</span>
        <div class="control-btn" style="margin-left:0;" @click="rotateVideo" title="向右旋转（快捷键 R）">
          <svg viewBox="0 0 24 24" width="32" height="32" style="display:block;" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="11" width="11" height="11" rx="1.2" />
            <path d="M5 14 C5 10, 8 6, 14 6" />
            <path d="M14 6 L11 4 M14 6 L11 8" />
          </svg>
        </div>
        <span class="scale-text"
              title="通过滚轮控制缩放大小">{{ (scale * 100).toFixed(0) }}%</span>
      </div>
      <!-- 视频缩略图列表 -->
      <div v-if="videos.length" class="video-thumbnails">
        <div
          v-for="(url, i) in videos"
          :key="i"
          class="video-thumb-item"
          :class="{ active: i === currentIndex }"
          @click="playVideoByIndex(i)">
          <video class="video-thumb-player" :src="url" preload="metadata" muted playsinline></video>
        </div>
      </div>
      <div v-if="videos.length > 1"
           class="video-nav-arrow video-nav-next"
           :class="{ disabled: currentIndex === videos.length - 1 }"
           @click="nextVideo">
        <i class="el-icon-arrow-right"></i>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'VideoPlayerDialog',
  props: {
    // 是否显示弹窗（支持 .sync）
    visible: {
      type: Boolean,
      default: false,
    },
    // 视频 URL 列表
    videos: {
      type: Array,
      default: () => [],
    },
    // 打开时默认播放的视频索引
    index: {
      type: Number,
      default: 0,
    },
  },
  data () {
    return {
      currentIndex: 0,
      currentUrl: '',
      scale: 1,
      rotation: 0,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      instantReset: false,
      dragStartX: 0,
      dragStartY: 0,
      startTranslateX: 0,
      startTranslateY: 0,
      isPlaying: false,
      videoCurrentTime: 0,
      videoDuration: 0,
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (val) {
        this.$emit('update:visible', val)
      },
    },
  },
  watch: {
    visible (val) {
      if (val) {
        this.currentIndex = Math.min(Math.max(this.index, 0), Math.max(this.videos.length - 1, 0))
        this.currentUrl = this.videos[this.currentIndex] || ''
        this.resetVideoState()
        this.$nextTick(() => {
          const wrapper = this.$refs.videoDialogWrapper
          wrapper && wrapper.focus()
        })
      }
    },
  },
  methods: {
    /**
     * 关闭弹窗时的清理
     */
    handleClose () {
      this.currentUrl = ''
      this.resetVideoState()
    },
    /**
     * ESC 关闭弹窗
     */
    closeDialog () {
      this.dialogVisible = false
    },
    /**
     * 重置视频播放状态
     */
    resetVideoState () {
      this.scale = 1
      this.rotation = 0
      this.translateX = 0
      this.translateY = 0
      this.isDragging = false
      this.isPlaying = false
      this.videoCurrentTime = 0
      this.videoDuration = 0
    },
    /**
     * @param {string} url 视频地址
     */
    goToVideo (url) {
      this.instantReset = true
      this.currentUrl = url
      this.resetVideoState()
      this.$nextTick(() => {
        this.instantReset = false
      })
    },
    /**
     * 切换到上一个视频
     */
    prevVideo () {
      if (this.currentIndex > 0) {
        this.currentIndex--
        this.goToVideo(this.videos[this.currentIndex] || '')
      }
    },
    /**
     * 切换到下一个视频
     */
    nextVideo () {
      if (this.currentIndex < this.videos.length - 1) {
        this.currentIndex++
        this.goToVideo(this.videos[this.currentIndex] || '')
      }
    },
    /**
     * 点击缩略图直接播放指定视频
     * @param {number} i 视频索引
     */
    playVideoByIndex (i) {
      if (i === this.currentIndex) return
      if (i < 0 || i >= this.videos.length) return
      this.currentIndex = i
      this.goToVideo(this.videos[i] || '')
    },
    /**
     * 视频滚轮缩放
     * @param {WheelEvent} e
     */
    onVideoWheel (e) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1
      let newScale = this.scale + delta
      newScale = Math.max(0.5, Math.min(5, newScale))
      this.scale = parseFloat(newScale.toFixed(2))
    },
    rotateVideo () {
      this.rotation += 90
    },
    /**
     * 开始拖拽视频
     * @param {MouseEvent} e
     */
    onDragStart (e) {
      if (e.button !== 0) return
      this.isDragging = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.startTranslateX = this.translateX
      this.startTranslateY = this.translateY
    },
    /**
     * 拖拽中
     * @param {MouseEvent} e
     */
    onDragMove (e) {
      if (!this.isDragging) return
      this.translateX = this.startTranslateX + (e.clientX - this.dragStartX) / this.scale
      this.translateY = this.startTranslateY + (e.clientY - this.dragStartY) / this.scale
    },
    /**
     * 结束拖拽
     */
    onDragEnd () {
      this.isDragging = false
    },
    /**
     * 切换播放/暂停
     */
    togglePlay () {
      const video = this.$refs.videoPlayer
      if (!video) return
      if (video.paused) {
        video.play()
        this.isPlaying = true
      } else {
        video.pause()
        this.isPlaying = false
      }
    },
    /**
     * 视频播放进度更新
     */
    onTimeUpdate () {
      const video = this.$refs.videoPlayer
      if (video) {
        this.videoCurrentTime = video.currentTime
      }
    },
    /**
     * 视频元数据加载完成
     */
    onLoadedMeta () {
      const video = this.$refs.videoPlayer
      if (video) {
        this.videoDuration = video.duration || 0
      }
    },
    /**
     * 视频播放结束
     */
    onVideoEnded () {
      this.isPlaying = false
    },
    /**
     * 点击进度条跳转
     * @param {MouseEvent} e
     */
    seekVideo (e) {
      const video = this.$refs.videoPlayer
      const track = e.currentTarget
      if (!video || !track || !this.videoDuration) return
      const rect = track.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      video.currentTime = ratio * this.videoDuration
      this.videoCurrentTime = video.currentTime
    },
    /**
     * 格式化视频时间
     * @param {number} seconds
     * @returns {string}
     */
    formatVideoTime (seconds) {
      if (!isFinite(seconds) || seconds < 0) return '00:00'
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0')
    },
  },
}
</script>

<!-- 全局样式：el-dialog 会被 appendToBody 到 body 下，scoped 样式无法命中，故放在全局并加前缀隔离 -->
<style lang="scss">
.video-player-dialog {
  &.el-dialog {
    background-color: rgba(0, 0, 0, 0.5);
    box-shadow: none !important;
    margin: 0 !important;
    width: 100% !important;
    height: 100vh;
    border-radius: 0;
  }

  .el-dialog__body {
    padding: 0;
    height: 100%;
    background-color: transparent !important;
  }

  .el-dialog__header {
    padding: 0;
  }

  .el-dialog__headerbtn {
    position: fixed;
    top: -40px;
    right: -40px;
    width: 80px;
    height: 80px;
    margin: 0;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    overflow: hidden;
    transition: background-color 0.15s;
    z-index: 2050;

    &:focus,
    &:hover {
      background-color: rgba(0, 0, 0, 0.8);
    }

    .el-dialog__close {
      position: absolute;
      bottom: 15px;
      left: 15px;
      color: #fff;
      font-size: 22px;
    }
  }

  .video-dialog-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    position: relative;
    padding: 0 60px;
    outline: none;
    overflow: hidden;
  }

  .video-dialog-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    padding-bottom: 130px;
    box-sizing: border-box;
    background-color: transparent !important;

    &.is-dragging {
      cursor: grabbing;
    }
  }

  .video-transform-wrapper {
    display: inline-block;
    position: relative;
    transition: transform 0.1s ease-out;
    transform-origin: center center;
    background-color: transparent !important;

    &.no-transition {
      transition: none;
    }
  }

  .video-player {
    margin-top: 10px;
    max-width: 100%;
    max-height: calc(100vh - 130px);
    width: auto;
    height: auto;
    outline: none;
    background-color: transparent;
    display: block;
    cursor: pointer;
  }

  .video-nav-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.3s, opacity 0.3s;
    z-index: 10;

    i {
      font-size: 22px;
      color: #fff;
      -webkit-text-stroke: 2px #fff;
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.4);
    }

    &.disabled {
      opacity: 0.3;
      cursor: not-allowed;

      &:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }
    }
  }

  .video-nav-prev {
    left: 12px;
  }

  .video-nav-next {
    right: 12px;
  }

  .custom-video-controls {
    position: absolute;
    bottom: 75px;
    left: 50%;
    transform: translateX(-50%);
    height: 40px;
    width: 50%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 16px;
    z-index: 20;
    user-select: none;

    .control-btn {
      font-size: 36px;
      color: #fff;
      cursor: pointer;
      flex-shrink: 0;
      transition: opacity 0.2s;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      margin-left: -10px;


    }

    .progress-track {
      flex: 1;
      height: 6px;
      background: rgba(255, 255, 255, 0.25);
      border-radius: 3px;
      cursor: pointer;
      position: relative;
      overflow: hidden;

      &:hover {
        height: 8px;
        border-radius: 4px;
      }
    }

    .progress-fill {
      height: 100%;
      background: #409eff;
      border-radius: inherit;
      pointer-events: none;
    }

    .time-text {
      font-size: 12px;
      color: #fff;
      white-space: nowrap;
      flex-shrink: 0;
      font-variant-numeric: tabular-nums;
    }

    .scale-text {
      font-size: 12px;
      color: #c0c4cc;
      white-space: nowrap;
      flex-shrink: 0;
      font-variant-numeric: tabular-nums;
      min-width: 32px;
      text-align: right;
    }
  }

  .video-thumbnails {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.5);
    overflow-x: auto;
    z-index: 20;
    user-select: none;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.3);
      border-radius: 3px;
    }

    .video-thumb-item {
      flex: 0 0 auto;
      width: 36px;
      height: 54px;
      border-radius: 4px;
      overflow: hidden;
      cursor: pointer;
      border: 2px solid transparent;
      opacity: 0.7;
      transition: border-color 0.2s, opacity 0.2s, transform 0.2s;

      &:hover {
        opacity: 1;
        transform: translateY(-2px);
      }

      &.active {
        border-color: #409eff;
        opacity: 1;
      }

      .video-thumb-player {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        background-color: #000;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/user-workinfo/components/VideoPlayerDialog.vue